import React, { useEffect, useState } from 'react';
import { RTFJS } from 'rtf.js';
import HighlighterWrapper from '@components/highlighterWrapper/HighlighterWrapper';
import EncryptUtils from 'src/utils/EncryptUtils';
import { SEARCH_CONTEXT } from 'src/routes/BaseRoute';
import { useLocation } from 'react-router-dom';
import { sanitizeHtml } from 'src/utils/htmlSanitizer';

interface RtfViewerProps {
  rtfBuffer: Buffer | ArrayBuffer;
}

const toArrayBuffer = (buffer: Buffer | ArrayBuffer) =>
  buffer instanceof ArrayBuffer
    ? buffer
    : buffer.buffer.slice(
        buffer.byteOffset,
        buffer.byteOffset + buffer.byteLength
      ) as ArrayBuffer;

type RenderMode = 'RTF' | 'PRE';

const RtfViewer: React.FC<RtfViewerProps> = ({ rtfBuffer }) => {
  const [htmlContent, setHtmlContent] = useState<Element[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const search_context = EncryptUtils.getDecryptedData(
    location?.search,
    SEARCH_CONTEXT
  );

  /* ---------------- Decode once ---------------- */
  const decodeRTF = (buffer: Buffer | ArrayBuffer): string =>
    new TextDecoder('utf-8').decode(buffer);

  /* ---------------- Detect layout-heavy RTF ---------------- */
  const analyzeRTF = (text: string): RenderMode => {
    const hasTables = /\\trowd\b|\\cellx\d+/.test(text);
    const hasTabs = /\\tx\d+/.test(text);
    const hasImages = /\{\\pict\b/.test(text);
    const hasManualSpacing = /\s{4,}/.test(text);

    return hasTables || hasTabs || hasImages || hasManualSpacing
      ? 'PRE'
      : 'RTF';
  };

  /* ---------------- Remove {\* ... } hidden destinations ---------------- */
  const removeHiddenDestinations = (rtf: string): string => {
    let result = rtf;
    const regex = /\{\\\*/g;

    let match: RegExpExecArray | null;
    while ((match = regex.exec(result))) {
      let start = match.index;
      let depth = 0;
      let i = start;

      for (; i < result.length; i++) {
        if (result[i] === '{') depth++;
        else if (result[i] === '}') {
          depth--;
          if (depth === 0) {
            i++;
            break;
          }
        }
      }

      result = result.slice(0, start) + result.slice(i);
      regex.lastIndex = 0;
    }

    return result;
  };

  /* ---------------- Remove {\pict ... } image blocks ---------------- */
  const removePictGroups = (rtf: string): string => {
    let result = rtf;
    const regex = /\{\\pict\b/gi;

    let match: RegExpExecArray | null;
    while ((match = regex.exec(result))) {
      let start = match.index;
      let depth = 0;
      let i = start;

      for (; i < result.length; i++) {
        if (result[i] === '{') depth++;
        else if (result[i] === '}') {
          depth--;
          if (depth === 0) {
            i++;
            break;
          }
        }
      }

      result = result.slice(0, start) + result.slice(i);
      regex.lastIndex = 0;
    }

    return result;
  };

  /* ---------------- Remove named RTF group safely ---------------- */
  const removeRTFGroup = (rtf: string, group: string): string => {
    const regex = new RegExp(`\\{\\\\${group}\\b`, 'gi');

    let match: RegExpExecArray | null;
    while ((match = regex.exec(rtf))) {
      let start = match.index;
      let depth = 0;
      let i = start;

      for (; i < rtf.length; i++) {
        if (rtf[i] === '{') depth++;
        else if (rtf[i] === '}') {
          depth--;
          if (depth === 0) {
            i++;
            break;
          }
        }
      }

      rtf = rtf.slice(0, start) + rtf.slice(i);
      regex.lastIndex = 0;
    }

    return rtf;
  };

  /* ---------------- PRE MODE renderer (clean text) ---------------- */
  const renderAsPreformatted = (text: string) => {
  let cleaned = text;


  cleaned = removeHiddenDestinations(cleaned);
  cleaned = removePictGroups(cleaned);

  const GROUPS = [
    'fonttbl',
    'colortbl',
    'stylesheet',
    'info',
    'rsidtbl',
    'listtable',
    'listoverridetable',
    'userprops'
  ];

  for (const g of GROUPS) {
    cleaned = removeRTFGroup(cleaned, g);
  }

  cleaned = cleaned

    .replace(/\\tab/g, '\t')
    .replace(/\\par[d]?/g, '\n')
 
    .replace(/\\'[0-9a-fA-F]{2}/g, '')

    .replace(/\\[a-zA-Z]+\d*/g, '')
    
    .replace(/[{}]/g, '');

  cleaned = cleaned.replace(
    /(^|\s)(?:-\d{2,4}){1,6}(?=\s|$)/g,
    ' '
  );

  const pre = document.createElement('pre');
  pre.innerText = cleaned;
  pre.style.whiteSpace = 'pre-wrap';
  pre.style.wordBreak = 'break-word';

  setHtmlContent([pre]);
};


  /* ---------------- RTFJS sanitizer ---------------- */
  const sanitizeRTFForRTFJS = (text: string): ArrayBuffer => {
    let rtf = text;

    rtf = removeHiddenDestinations(rtf);
    rtf = removePictGroups(rtf);

    const GROUPS = [
      'fonttbl',
      'colortbl',
      'stylesheet',
      'info',
      'rsidtbl',
      'listtable',
      'listoverridetable',
      'userprops'
    ];

    for (const g of GROUPS) {
      rtf = removeRTFGroup(rtf, g);
    }

    // Remove hidden text runs
    rtf = rtf.replace(/\\v\b[\s\S]*?\\v0\b/g, '');

    const encoded = new TextEncoder().encode(rtf);
    return encoded.buffer.slice(
      encoded.byteOffset,
      encoded.byteOffset + encoded.byteLength
    );
  };

  /* ---------------- Render pipeline ---------------- */
  useEffect(() => {
    const run = async () => {
      try {
        const rawText = decodeRTF(toArrayBuffer(rtfBuffer));
        const mode = analyzeRTF(rawText);

        if (mode === 'PRE') {
          renderAsPreformatted(rawText);
          setError(null);
          return;
        }

        // Best-effort HTML render
        const cleanBuffer = sanitizeRTFForRTFJS(rawText);
        const doc = new RTFJS.Document(cleanBuffer, {});
        const rendered = await doc.render();

        setHtmlContent(rendered);
        setError(null);
      } catch (e) {
        console.error('RTF render failed, falling back to PRE:', e);
        try {
          renderAsPreformatted(decodeRTF(toArrayBuffer(rtfBuffer)));
          setError(null);
        } catch {
          setError('Unsupported document format');
        }
      }
    };

    run();
  }, [rtfBuffer]);

  /* ---------------- UI ---------------- */
  return (
    <HighlighterWrapper keywords={[search_context]}>
      <div style={{ overflow: 'auto', height: '100%', width: '100%' }}>
        {error ? (
          <div style={{ color: 'red' }}>{error}</div>
        ) : (
          htmlContent && (
            <div style={{ width: '100%', maxWidth: '45vw' }}>
              {htmlContent.map((el, idx) => (
                <div
                  key={idx}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(el.outerHTML) }}
                />
              ))}
            </div>
          )
        )}
      </div>
    </HighlighterWrapper>
  );
};

export default RtfViewer;
